import { useRef } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import { FilePreviewModal } from "../../../../components/filePreview/FilePreviewModal";
import { SUPPORTED_DOCUMENT_ACCEPT } from "../../../../utils/fileValidation";
import {
  ChecklistItem,
  getUploadedRequiredDocument,
} from "../../../../utils/requiredDocuments";
import DocumentActionIcons from "./DocumentActionIcons";
import { useDocumentActions } from "./useDocumentActions";

function UploadAction({ label, disabled, onFile }: any) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORTED_DOCUMENT_ACCEPT}
        style={{ display: "none" }}
        data-testid={`required-doc-input-${label}`}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) onFile(files);
        }}
      />
      <IconButton
        aria-label={t("uploadRequiredDocument", { label })}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        sx={{ border: "1px solid #1B1464", borderRadius: "8px" }}
      >
        <UploadIcon fontSize="small" />
      </IconButton>
    </>
  );
}

export default function RequiredDocuments(props: any) {
  const { t } = useTranslation();
  const {
    checklist,
    documentList,
    setDocumentList,
    dynamicConfig,
    uploadDocument,
    isLoanSubmitted,
  } = props;

  const { preview, viewFile, handleDownloadDocument, handleDeleteDocument } =
    useDocumentActions({
      dynamicConfig,
      documentList,
      setDocumentList,
      getChecklistPayload: props.getChecklistPayload,
    });
  const { previewData, loading, error, closePreview } = preview;

  const required: ChecklistItem[] = checklist ?? [];
  if (!required.length) return null;
  const uploadedCount = required.filter((r) =>
    getUploadedRequiredDocument(documentList, r.key),
  ).length;

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1px solid #DBE2FF",
        background: "#FFF",
        width: "50%",
        flex: "1 1 0",
        minWidth: 0,
      }}
      data-testid="required-documents"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#F5F6FA",
          padding: "24px 24px 8px 24px"
        }}
      >
        <h3
          style={{
            fontFamily: "Poppins",
            fontSize: 16,
            fontWeight: 700,
            margin: "0 0 8px",
            color: "#32325d"
          }}
        >
          {t("requiredDocuments")}
        </h3>
        <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
          {t("requiredDocsUploadedCount", {
            uploaded: uploadedCount,
            total: required.length,
          })}
        </span>
      </div>

      <div style={{ padding: "0 24px 24px 24px" }}>
        {required.map(({ key, label }) => {
          const doc = getUploadedRequiredDocument(documentList, key);
          return (
            <Box
              key={key}
              data-testid={`required-doc-${key}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.5,
                borderTop: "1px solid #EEF0F6",
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: doc ? "#E3F4F1" : "#F1F2F6",
                  color: "#2E8B83",
                }}
              >
                {doc ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <DescriptionOutlinedIcon
                    fontSize="small"
                    sx={{ color: "#888" }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  title={doc?.documentName ?? label}
                  sx={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 500 }}
                >
                  {label}
                </Typography>
                {doc ? (
                  <Typography
                    noWrap
                    sx={{ fontFamily: "Poppins", fontSize: 12, color: "#666" }}
                  >
                    {doc.documentName} ·{" "}
                    {doc.date
                      ? moment(doc.date).format("MM/DD/YYYY · hh:mm A")
                      : ""}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: 12,
                      color: "#B3261E",
                    }}
                  >
                    {t("requiredLabel")}
                  </Typography>
                )}
              </Box>
              {doc ? (
                <DocumentActionIcons
                  doc={doc}
                  onView={viewFile}
                  onDownload={handleDownloadDocument}
                  onDelete={handleDeleteDocument}
                  disableDelete={isLoanSubmitted}
                />
              ) : (
                <UploadAction
                  label={label}
                  disabled={isLoanSubmitted}
                  onFile={(files: File[]) => uploadDocument?.(files, key)}
                />
              )}
            </Box>
          );
        })}
      </div>
      <FilePreviewModal
        open={loading || !!previewData || !!error}
        loading={loading}
        error={error}
        previewData={previewData}
        onClose={closePreview}
      />
    </Box>
  );
}
